import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * Root-level error boundary.
 * Catches any unhandled React render/lifecycle errors and shows a recovery
 * screen instead of a blank white page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for debugging without crashing
    try {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    } catch {}
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error ? this.state.error.toString() : 'Unknown error';

    return (
      <div
        className="min-h-screen bg-[#0f1a2e] flex flex-col items-center justify-center p-6 text-center"
        data-testid="error-boundary-screen"
      >
        <div className="w-14 h-14 rounded-full bg-rose-500/15 flex items-center justify-center mb-5">
          <AlertTriangle size={28} className="text-rose-400" />
        </div>
        <h1 className="font-display text-xl font-bold text-white mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-white/60 mb-4 max-w-xs">
          The app encountered an unexpected error. Tap below to reload.
        </p>
        {/* Show error to help diagnose production issues */}
        <pre className="mb-6 text-[10px] text-rose-300/70 max-w-xs text-left overflow-auto max-h-32 bg-black/30 rounded-lg px-3 py-2 w-full">
          {errMsg.slice(0, 400)}
        </pre>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-5 py-3 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-xl text-sm font-display font-bold transition-colors"
          data-testid="error-boundary-reload-btn"
        >
          <RefreshCw size={15} />
          Reload App
        </button>
      </div>
    );
  }
}
